<template>
  <div v-click-outside="closeHanler">
    <div class="filter-btn" @click="openHanlder">
      <div class="filter-btn__title">
        <span>{{ title }}</span>
        <span>{{ computedFilterTitle }}</span>
      </div>
      <div class="filter-btn__icons">
        <CloseRoundIcon
          :onClose="resetFilter"
          v-if="isActiveFilter"
          class="filter-btn__icons-reset"
        />
        <span v-if="isActiveFilter" class="devider"></span>
        <CalendarIcon />
      </div>
    </div>
    <Transition name="fade">
      <div v-if="isOpen" class="date-modal">
        <div v-if="display.smAndDown" class="mobile-header">
          <span>{{ title }}</span>
          <div @click="closeHanler" class="mobile-header__icon">
            <CloseBoldIcon />
          </div>
        </div>
        <div v-if="!display.smAndDown" class="date-modal__header">
          <ArrowIcon
            :onClick="() => moveHandler('prev')"
            class="arrow-icon arrow-icon--left"
            :class="{ 'arrow-icon--disabled': isDisablePrev }"
          />
          <span>{{ monthLabel }}</span>
          <ArrowIcon
            :onClick="() => moveHandler('next')"
            class="arrow-icon arrow-icon--right"
          />
        </div>
        <div class="mobile-wrapper">
          <div class="date-modal__change">
            <div
              @click="calendarMode = 'mulitiple'"
              :class="[calendarMode !== 'range' && '--selected']"
            >
              Несколько дат
            </div>
            <div
              @click="calendarMode = 'range'"
              :class="[calendarMode === 'range' && '--selected']"
            >
              Диапазон дат
            </div>
          </div>
          <Calendar
            v-model="calendarValue"
            :range-value="calendarRangeValue"
            @onRangeValue="($event) => (calendarRangeValue = $event)"
            :mode="calendarMode"
            :availableDates="availableDates"
            ref="calendarRef"
            @to-page="
              (v) => (display.smAndDown ? $emit('to-page', v) : undefined)
            "
          />
        </div>
        <div class="date-modal__button">
          <span
            v-if="availableDates && !availableDates.length"
            style="white-space: wrap"
            >Нет доступных дат для записи. Попробуйте изменить настроенные
            фильтры</span
          >
          <span v-else> {{ comuptedDatesName }}</span>
          <Button :disabled="isDisabledApplyButton" :onClick="applyFilter"
            >Применить</Button
          >
        </div>
      </div>
    </Transition>
  </div>
</template>
<script>
import { parseISO } from 'date-fns'
import { computed, onMounted, ref, toRefs, watch } from '@vue/composition-api'

import { capitalize } from '@/core/helpers'

import Calendar from './components/calendar'
import Button from '@/components/button'

import CloseRoundIcon from '@/assets/icons/close-round'
import CalendarIcon from '@/assets/icons/calendar'
import ArrowIcon from '@/assets/icons/arrow'
import CloseBoldIcon from '@/assets/icons/close-bold'
import { MONTH_NAMES } from '@/core/constants'
import { useDisplay } from '@/hooks'
import { formatWithLocale } from '@/core/utils'

export default {
  name: 'date-filter',
  components: {
    CloseRoundIcon,
    CalendarIcon,
    ArrowIcon,
    Calendar,
    Button,
    CloseBoldIcon
  },
  props: ['value', 'availableDates', 'disablePrev', 'title'],
  setup(props, ctx) {
    const { value, disablePrev } = toRefs(props)
    const display = useDisplay()

    const month =
      MONTH_NAMES[new Date().getMonth() + 1] +
      formatWithLocale(new Date(), ' yyyy г.')

    const selectedItems = []
    const calendarMode = ref('mulitiple')

    const isDisabledApplyButton = computed(
      () => comuptedDatesName.value === 'Выберите даты'
    )

    watch(calendarMode, (newVal) => {
      calendarMode.value = newVal
      monthLabel.value = month
      if (disablePrev.value) {
        isDisablePrev.value = true
      }
    })

    watch(value, (newVal) => {
      if (newVal.calendarMode === 'mulitiple') {
        calendarValue.value = newVal.value
        calendarMode.value = 'mulitiple'
      }

      if (newVal.calendarMode === 'range') {
        calendarRangeValue.value = newVal.value
        calendarMode.value = 'range'
      }
    })

    onMounted(() => {
      if (!value.value) return
      if (value.value.calendarMode === 'mulitiple') {
        calendarValue.value = value.value.value
        calendarMode.value = 'mulitiple'
      }
      if (value.value.calendarMode === 'range') {
        calendarRangeValue.value = value.value.value
        calendarMode.value = 'range'
      }
    })

    let stashSelected = null
    const initialValue = [
      [],
      {
        start: null,
        end: null
      }
    ]

    const isOpen = ref(false)
    const calendarRef = ref(null)
    const calendarValue = ref(initialValue[0])
    const calendarRangeValue = ref(initialValue[1])
    const monthLabel = ref(capitalize(month))
    const isDisablePrev = ref(disablePrev.value)

    const isActiveFilter = computed(
      () => comuptedDatesName.value !== 'Выберите даты'
    )

    const computedFilterTitle = computed(() => {
      if (comuptedDatesName.value === 'Выберите даты') return 'Все'
      return comuptedDatesName.value
    })

    const comuptedDatesName = computed(() => {
      if (calendarMode.value === 'range') {
        const start = calendarRangeValue.value.start
        const end = calendarRangeValue.value.end
        if (!start && !end) {
          return 'Выберите даты'
        }

        if (start === end) {
          const formatToday = formatWithLocale(parseISO(start), 'dd MMMM')

          return `Сегодня, ${formatToday}`
        }

        const monthNameStart = formatWithLocale(parseISO(start), 'MMMM')
        const monthNameEnd = formatWithLocale(parseISO(end), 'MMMM')
        const startDay = formatWithLocale(parseISO(start), 'dd')
        const endDay = formatWithLocale(parseISO(end), 'dd')

        if (monthNameStart === monthNameEnd) {
          return `${startDay} \u2014 ${endDay} ${monthNameStart}`
        } else {
          return `${startDay} ${monthNameStart} \u2014 ${endDay} ${monthNameEnd}`
        }
      }

      const now = formatWithLocale(new Date(), 'yyyy-MM-dd')
      if (!calendarValue.value.length) return 'Выберите даты'
      if (calendarValue.value.length === 1) {
        const isNow = calendarValue.value[0] === now
        const day = formatWithLocale(
          parseISO(calendarValue.value[0]),
          'dd MMMM'
        )

        return isNow ? `Сегодня, ${day}` : day
      } else {
        const datesMap = {}
        calendarValue.value.forEach((el) => {
          const day = el.split('-')[2]
          const month = formatWithLocale(parseISO(el), 'MMMM')

          if (!(month in datesMap)) {
            datesMap[month] = []
          }
          datesMap[month].push(day)
        })

        let result = ''
        let index = 0
        const countMonth = Object.keys(datesMap)
        for (const month in datesMap) {
          index++

          const el = datesMap[month]
          result += `${el.sort().join(',')} ${month}`
          if (index !== countMonth.length) result += ', '
        }

        return result
      }
    })

    function openHanlder() {
      if (isOpen.value) {
        if (stashSelected.calendarMode === 'mulitiple') {
          calendarValue.value = stashSelected.value
          calendarRangeValue.value = initialValue[1]
          calendarMode.value = 'mulitiple'
        }

        if (stashSelected.calendarMode === 'range') {
          calendarValue.value = initialValue[0]
          calendarRangeValue.value = stashSelected.value
          calendarMode.value = 'range'
        }
      }

      if (value.value) {
        stashSelected = value.value
      } else {
        stashSelected = {
          calendarMode: 'mulitiple',
          value: initialValue[0]
        }
      }
      monthLabel.value = capitalize(month)
      if (disablePrev.value) {
        isDisablePrev.value = true
      }

      isOpen.value = !isOpen.value
    }

    function closeHanler() {
      if (!isOpen.value) return

      if (stashSelected.calendarMode === 'mulitiple') {
        calendarValue.value = stashSelected.value
        calendarRangeValue.value = initialValue[1]
        calendarMode.value = 'mulitiple'
      }

      if (stashSelected.calendarMode === 'range') {
        calendarValue.value = initialValue[0]
        calendarRangeValue.value = stashSelected.value
        calendarMode.value = 'range'
      }

      isOpen.value = false
    }

    function moveHandler(dir) {
      const nowDate = formatWithLocale(new Date(), 'yyyyM')
      const calendarNode =
        calendarMode.value === 'range'
          ? calendarRef.value.$children[0].$children[0]
          : calendarRef.value.$children[0]
      const calendarPage = calendarNode.pages[0]

      if (dir === 'next') {
        const calendarDate = `${calendarPage.nextMonthComps.year}${calendarPage.nextMonthComps.month}`
        monthLabel.value = `${MONTH_NAMES[calendarPage.nextMonthComps.month]} ${
          calendarPage.year
        }г`

        if (nowDate === calendarDate && disablePrev.value) {
          isDisablePrev.value = true
        }

        calendarNode.moveNext()
        if (disablePrev.value) {
          isDisablePrev.value = false
        }
      }

      if (dir === 'prev') {
        const calendarDate = `${calendarPage.prevMonthComps.year}${calendarPage.prevMonthComps.month}`
        monthLabel.value = `${MONTH_NAMES[calendarPage.prevMonthComps.month]} ${
          calendarPage.year
        }г`

        if (nowDate === calendarDate && disablePrev.value) {
          isDisablePrev.value = true
        }

        calendarNode.movePrev()
      }

      ctx.emit('moveMonth', {
        direction: dir,
        date:
          dir === 'next'
            ? calendarPage.nextMonthComps
            : calendarPage.prevMonthComps
      })
    }

    function resetFilter() {
      calendarValue.value = initialValue[0]
      calendarRangeValue.value = initialValue[1]
      calendarMode.value = 'multiple'
      ctx.emit('input', null)
      ctx.emit('reset-filter')
    }

    function applyFilter() {
      isOpen.value = false

      if (calendarMode.value === 'range') {
        ctx.emit('input', {
          calendarMode: calendarMode.value,
          value: calendarRangeValue.value
        })
        ctx.emit('submit')
      } else {
        ctx.emit('input', {
          calendarMode: calendarMode.value,
          value: calendarValue.value
        })
        ctx.emit('submit')
      }
    }

    return {
      selectedItems,
      isOpen,
      moveHandler,
      calendarRef,
      closeHanler,
      calendarValue,
      comuptedDatesName,
      monthLabel,
      isDisablePrev,
      calendarMode,
      calendarRangeValue,
      resetFilter,
      applyFilter,
      isActiveFilter,
      isDisabledApplyButton,
      display,
      computedFilterTitle,
      openHanlder
    }
  }
}
</script>
<style lang="scss" scoped>
@import 'style';
@import '../clinicFilter/style';
</style>
